import { useCallback, useEffect, useRef } from 'react'

import { useNavigate } from '@tanstack/react-router'

import { useBottomSheetContext } from '@/shared/contexts/BottomSheetContext'
import { useDigitalProductContextOptional } from '@/shared/contexts/DigitalProductContext'
import { api } from '@/shared/lib/api'
import { usePrizesStore } from '@/shared/store/usePrizesStore'

import { DigitalProductPrizeBottomSheet } from '../BottomSheet'
import {
  clearPrizeSheetSearch,
  isPrizeSheetSearchActive,
  type PrizeSheetSearch
} from '../lib/prizeSheetSearch'

type UsePrizeSheetFromUrlOptions = {
  sheetSearch: PrizeSheetSearch
}

type PrizeRecord = { id: number } & Record<string, unknown>

/**
 * Sincroniza `/sorteios?concurso=` com o bottom sheet do concurso.
 * Ver `design-system/patterns/url-driven-sheets.md`.
 *
 * É por aqui que o aviso de vitória da tela inicial chega ao detalhe: ele
 * navega para o endereço, e a rota abre o sheet. Abrir por `showBottomSheet`
 * direto da home não funcionaria — o provider de sheet vive dentro desta tela,
 * e a URL ficaria velha.
 *
 * O registro vem do endereço **autenticado**, e não do snapshot público, como
 * decidiu `design.md §4.3`: "o detalhe reconcilia com o mesmo dado autenticado
 * em vez de esperar o build". O snapshot de `/gamification/data` é servido sem
 * autenticação, cacheado em CDN e só reconstruído depois do anúncio — quem
 * acabou de ganhar e clicou no aviso abriria o concurso ainda em aberto,
 * `executed: false` e `winners: []`.
 *
 * A reconciliação é por `GET /user/digital_products/:id/prizes/:id`, e não pelo
 * próprio `prize_announcements`, por duas razões medidas contra a api de dev:
 *
 * 1. `Prize::AnnouncementSerializer` emite `id`, `name`, `announced_at` e
 *    `media` — e nada mais. Sem `executed`, sem `prize_rules` e sem `winners`
 *    não há como montar o sheet. (O `include: 'winners.user'` do controller não
 *    muda isso: a associação também precisa estar declarada no serializer.)
 * 2. O aviso some da lista assim que a ciência é registrada, e o clique
 *    registra ciência **antes** de navegar (`WinnerAnnouncements`). Ler dali
 *    seria correr contra a própria ciência que o clique acabou de disparar.
 *
 * O endereço de concurso não tem nenhum dos dois problemas: desce o registro
 * inteiro e não muda de estado quando a ciência é dada.
 */
export const usePrizeSheetFromUrl = ({
  sheetSearch
}: UsePrizeSheetFromUrlOptions) => {
  const navigate = useNavigate()
  const { prizes } = usePrizesStore()
  const digitalProductId =
    useDigitalProductContextOptional()?.digitalProductId ?? null
  const { showBottomSheet, hideBottomSheet } = useBottomSheetContext() as {
    showBottomSheet: (props: Record<string, unknown>) => void
    hideBottomSheet: () => void
  }
  const openedIdRef = useRef<number | null>(null)
  const requestedIdRef = useRef<number | null>(null)

  /* Memoizado porque viaja como `onHide` para dentro do sheet: recriá-lo a cada
     render trocaria a prop do sheet aberto sem nenhum motivo. */
  const clearSheetSearch = useCallback(() => {
    void navigate({
      to: '/sorteios',
      replace: true,
      search: clearPrizeSheetSearch() as never
    })
  }, [navigate])

  /* Primitivo, e não o objeto de search: `sheetSearch` pode trocar de
     identidade a cada render do roteador, e o efeito abaixo dispara rede. */
  const activeId = isPrizeSheetSearchActive(sheetSearch)
    ? sheetSearch.concurso
    : null

  useEffect(() => {
    if (activeId == null) {
      requestedIdRef.current = null

      if (openedIdRef.current !== null) {
        hideBottomSheet()
        openedIdRef.current = null
      }
      return undefined
    }

    // Já aberto, ou já em voo: o efeito re-roda a cada atualização do snapshot
    // e não pode abrir de novo nem pedir de novo.
    if (openedIdRef.current === activeId || requestedIdRef.current === activeId)
      return undefined

    requestedIdRef.current = activeId
    let cancelled = false

    const openWith = (record: PrizeRecord) => {
      // Dois cliques seguidos em concursos diferentes podem responder fora de
      // ordem. Só abre quem ainda é o pedido corrente.
      if (cancelled || requestedIdRef.current !== activeId) return

      openedIdRef.current = activeId
      showBottomSheet({
        component: DigitalProductPrizeBottomSheet,
        ...record,
        onHide: clearSheetSearch
      })
    }

    const reconcile = async () => {
      const fromSnapshot = prizes.find(
        (candidate: { id: number }) => candidate.id === activeId
      ) as unknown as PrizeRecord | undefined

      if (digitalProductId) {
        try {
          const { data } = await api.get<{ record?: PrizeRecord }>(
            `/user/digital_products/${digitalProductId}/prizes/${activeId}`
          )

          if (data?.record) {
            openWith(data.record)
            return
          }
        } catch {
          // Falha de rede ou concurso fora do alcance deste participante. O
          // snapshot é pior — pode estar velho — mas é melhor que nada.
        }
      }

      if (fromSnapshot) {
        openWith(fromSnapshot)
        return
      }

      // Nem autenticado nem snapshot: libera o pedido para que a chegada do
      // conteúdo do evento re-dispare o efeito, em vez de errar agora.
      if (!cancelled && requestedIdRef.current === activeId) {
        requestedIdRef.current = null
      }
    }

    void reconcile()

    return () => {
      cancelled = true

      // Nada foi aberto: devolve o pedido para que a próxima execução do efeito
      // (troca de concurso, ou o duplo-disparo do StrictMode) tente de novo.
      if (openedIdRef.current !== activeId) requestedIdRef.current = null
    }
  }, [
    activeId,
    prizes,
    digitalProductId,
    showBottomSheet,
    hideBottomSheet,
    clearSheetSearch
  ])
}
